import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BaseModalComponent } from 'components/modal/base-modal.component';
import { formatDate, formatDatetime, formatTime } from 'util/date.utility';
import { ServiceSummaryComponent } from '../../service-summary/service-summary.component';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-reading-room-submission-initial-values-modal',
  standalone: true,
  imports: [TranslateModule, ServiceSummaryComponent, FileUploadComponent, CommonModule],
  templateUrl: './reading-room-submission-initial-values-modal.component.html',
  styleUrl: './reading-room-submission-initial-values-modal.component.scss'
})
export class ReadingRoomSubmissionInitialValuesModalComponent extends BaseModalComponent implements OnInit {
  formatDatetime = formatDatetime;
  formatTime = formatTime;
  @ViewChild('content') content: any;
  @Input() entry: any;
  entryJson?: string;
  oldValues: any;
  newValues: any;
  formatDate = formatDate;
  ngOnInit(): void {
    this.oldValues = this.tryParse(this.entry.oldValues);
    this.newValues = this.tryParse(this.entry.newValues);

    this.entryJson = JSON.stringify({ oldValues: this.oldValues, newValues: this.newValues }, null, 4);
  }

  show() {
    this.open(this.content);
  }

  tryParse(value: any) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return {};
    }
  }

  keysToLowercase(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.keysToLowercase(item));
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc: any, key: string) => {
        const newKey = key.charAt(0).toLowerCase() + key.slice(1);
        acc[newKey] = this.keysToLowercase(obj[key]);
        return acc;
      }, {});
    }
    return obj;
  }

  formatDateTimes() {
    const getText = (values: any) => {
      const time = !values.VisitTimeTo
        ? formatTime(values.VisitTimeFrom)
        : `${formatTime(values.VisitTimeFrom)} - ${formatTime(values.VisitTimeTo)}`;

      return `${formatDatetime(values.VisitDate)} (${time})`;
    };

    let reason = '';

    if (this.newValues?.Reason) {
      reason = `${this.newValues.Reason}`;
    }

    if (this.oldValues?.VisitDate) {
      return `Mainīts no ${getText(this.oldValues)} uz ${getText(this.newValues)}. Pamatojums: ${reason}`;
    }

    return `${getText(this.newValues)}${reason}`;
  }

  private readonly ORDER: string[] = ['Name', 'Surname', 'Email', 'Phone', 'Address'];

  formatMainInfoChanges(): string {
    const oldV = this.oldValues || {};
    const newV = this.newValues || {};

    const ALLOWED = new Set(['Email', 'Phone', 'Address', 'Name', 'Surname']);

    const allKeys = Array.from(new Set([...Object.keys(oldV), ...Object.keys(newV)])).filter((k) => ALLOWED.has(k));

    const ordered = [
      ...this.ORDER.filter((k) => allKeys.includes(k)),
      ...allKeys.filter((k) => !this.ORDER.includes(k)).sort((a, b) => a.localeCompare(b))
    ];

    const changes: string[] = [];

    for (const key of ordered) {
      const oldVal = this.prettyValue(key, oldV?.[key] ?? '');
      const newVal = this.prettyValue(key, newV?.[key] ?? '');
      if (oldVal === newVal) continue;

      const label = this.LABELS[key] ?? key;

      if (oldVal && newVal) {
        changes.push(`${label}: '${oldVal}' mainīts uz '${newVal}'`);
      } else if (!oldVal && newVal) {
        changes.push(`${label}: iestatīts '${newVal}'`);
      } else if (oldVal && !newVal) {
        changes.push(`${label}: '${oldVal}' noņemts`);
      }
    }

    return changes.length ? changes.join(', ') : 'Nav izmaiņu.';
  }

  private prettyValue(_: string, value: any): string {
    return value == null ? '' : String(value);
  }

  private readonly LABELS: Record<string, string> = {
    Email: 'E-pasts',
    Phone: 'Telefona numurs',
    Address: 'Adrese',
    Name: 'Vārds',
    Surname: 'Uzvārds',
    PersonalCode: 'Personas kods'
  };

  private extractBackofficeNote(raw: any): string {
    const obj = this.tryParse(raw);
    if (!obj || typeof obj !== 'object') return '';

    const note = obj.BackofficeNote ?? obj?.FileNote?.BackofficeNote ?? '';

    return typeof note === 'string' ? note.trim() : '';
  }

  isBackofficeNoteChanged(entry: any): boolean {
    const oldNote = this.extractBackofficeNote(entry?.oldValues);
    const newNote = this.extractBackofficeNote(entry?.newValues);
    return oldNote !== newNote;
  }

  private isIndefiniteOld(entry: any): boolean {
    const o = this.tryParse(entry?.oldValues);
    return (o?.TermType ?? '').toString().trim().toLowerCase() === 'beztermiņa';
  }

  private isIndefiniteNew(entry: any): boolean {
    const n = this.tryParse(entry?.newValues);
    const t = (n?.FileNote?.TermType ?? '').toString().trim().toLowerCase();
    return t === 'indefinite';
  }

  private getOldDate(entry: any): string {
    const o = this.tryParse(entry?.oldValues);
    return this.formatDate(o?.ValidTill ?? '');
  }

  private getNewDate(entry: any): string {
    const n = this.tryParse(entry?.newValues);
    return this.formatDate(n?.FileNote?.ValidTill ?? '');
  }

  isTermChanged(entry: any): boolean {
    const oldIndef = this.isIndefiniteOld(entry);
    const newIndef = this.isIndefiniteNew(entry);
    if (oldIndef !== newIndef) return true;

    if (!oldIndef && !newIndef) {
      return this.getOldDate(entry) !== this.getNewDate(entry);
    }

    return false;
  }

  formatTermChange(entry: any): string {
    const oldIndef = this.isIndefiniteOld(entry);
    const newIndef = this.isIndefiniteNew(entry);

    if (oldIndef && newIndef) return 'Nav izmaiņu.';

    if (oldIndef && !newIndef) {
      const nd = this.getNewDate(entry);
      return `Mainīts no 'Beztermiņa' uz '${this.formatDate(nd)}'`;
    }

    if (!oldIndef && newIndef) {
      const od = this.getOldDate(entry);
      return `Mainīts no '${od}' uz 'Beztermiņa'`;
    }

    const od = this.getOldDate(entry);
    const nd = this.getNewDate(entry);
    return `Mainīts no '${od}' uz '${this.formatDate(nd)}'`;
  }

  formatProcessingNotesChange(): string {
    const oldText = typeof this.entry?.oldValues === 'string' ? this.entry.oldValues.trim() : '';
    const newText = typeof this.entry?.newValues === 'string' ? this.entry.newValues.trim() : '';

    if (oldText && newText) {
      if (oldText === newText) return 'Nav izmaiņu.';
      return `Apstrādes piezīme: "${oldText}", mainīta uz: "${newText}".`;
    }

    if (!oldText && newText) {
      return `Pievienota jauna apstrādes piezīme: "${newText}".`;
    }

    if (oldText && !newText) {
      return `Dzēsta apstrādes piezīme: "${oldText}".`;
    }

    return 'Nav izmaiņu.';
  }
}
